import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { loginWithOneTimeAuthToken } from '@shared/api-endpoints'
import useAuthStore from '@shared/stores/useAuthStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import { datadogLogs } from '@datadog/browser-logs'

/** Own token exchange before either app or assessment module handles the route. */
export default async function oneTimeTokenGuard(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | false | undefined> {
  if (!Object.prototype.hasOwnProperty.call(to.query, 'one_time_token')) return

  const { one_time_token: token, ...query } = to.query
  const authStore = useAuthStore()
  const userStore = useUserStore()
  // Clean the existing browser entry first, preserving Vue Router's history state.
  // For an in-app navigation, do not replace the current page with the target.
  const url = new URL(window.location.href)
  if (url.searchParams.has('one_time_token')) {
    url.searchParams.delete('one_time_token')
    window.history.replaceState(window.history.state, '', url)
  }

  // An explicit new token must never fall back to the previous account.
  authStore.invalidateTokens()
  userStore.user = null
  if (typeof token === 'string' && token.trim().length > 0) {
    try {
      await loginWithOneTimeAuthToken(token.trim())
      return { path: to.path, query, hash: to.hash, replace: true }
    } catch {
      // The API layer logs the request failure. Do not continue routing.
    }
  } else {
    datadogLogs.logger.error('webcheckout_token_bootstrap_failed', {
      reason: 'invalid_token_parameter',
    })
  }

  authStore.invalidateTokens()
  userStore.user = null
  authStore.hasAuthError = true
  return false
}
